import * as React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import {lazyInject} from "@athena-portal/core";
import {
    ICustomerPortalService,
    resourcesHelper,
    Types,
    CoverChange, InteractionHelper
} from "@falcon/services";
import {getCoverTierWording} from "../../../../../CommonFunctions";

interface IState {
    isLoading: boolean;
    coverChanges: CoverChange[];
}

interface IProps {
    policyNumber: string,
    policyEndDate: string,
    upcomingMtaDate?: string;
    isEligibleForEnhancedCourtesyCar?: boolean;
}

type ModificationContext = {
    added: CoverChange[];
    removed: CoverChange[];
    isIneligibleForEnhancedCourtesyCarReplacedByEligible: boolean;
    isEligibleForEnhancedCourtesyCarReplacedByIneligible: boolean;
}

type Rule = {
    when: (context: ModificationContext) => boolean;
    then: (context: ModificationContext) => string;
}

export class FutureMTAExplainedHeader extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    private readonly isBritannia: boolean;

    constructor(props: any) {
        super(props);

        this.isBritannia = resourcesHelper.THEME === "Britannia";

        this.state = {
            isLoading: true,
            coverChanges: []
        };
    }

    public async componentDidMount() {
        this.setState({isLoading: true});
        this.customerPortalService.getCoverChanges(this.props.policyNumber).subscribe(
            (coverChanges: CoverChange[]) => {
                this.setState({
                    coverChanges,
                    isLoading: false
                })
            }
        );
    }

    public render() {
        if (!this.state.isLoading && this.props.upcomingMtaDate && this.state.coverChanges) {
            const mtaOnEndDateOfPolicy: boolean = moment.utc(this.props.policyEndDate).isSame(moment.utc(this.props.upcomingMtaDate), 'day');
            const additionalDescription: any = mtaOnEndDateOfPolicy ?
                (<>
                    You can't make any additional changes to this policy as the existing change date is your policy
                    expiry date.
                    <br/>
                    If you have renewed your policy you can make a change to your new policy from its policy start date
                </>) :
                "The earliest opportunity to make additional changes is " + this.getNextPossibleMtaDate(this.props.upcomingMtaDate, "DD/MM/YYYY")
            if (this.state.coverChanges && this.state.coverChanges.length !== 0) {
                return (
                    <div className="banner banner-info banner-info-multiple-paragraphs">
                        {this.hasChangesStartingToday()
                            ? "Heads up - you have already made changes to take effect from today."
                            : "Heads up - you have made changes to take effect from future dates:"}
                        {this.generateCoverChangeText(this.state.coverChanges)}
                        <div className="grid-col-2 margin-top-16"><strong>{additionalDescription}</strong></div>
                    </div>
                );
            } else if (moment(this.props.upcomingMtaDate).isBefore(moment().utc())) {
                return (
                    <div className="banner">
                        <img src={`/${resourcesHelper.THEME}/img/alert-blue.svg`} alt="Blue alert icon"/>
                        <div className="banner-text">
                            <p>Heads up - {additionalDescription}</p>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className="banner">
                        <img src={`/${resourcesHelper.THEME}/img/alert-blue.svg`} alt="Blue alert icon"/>
                        <div className="banner-text">
                            <p><strong>You have a future change that takes effect
                                on {this.convertDate(this.props.upcomingMtaDate, "DD/MM/YYYY")}</strong>
                            </p>
                            <p>{additionalDescription}</p>
                        </div>
                    </div>
                );
            }
        } else {
            return <></>;
        }
    }

    public generateCoverChangeText(coverChanges: CoverChange[]): React.ReactNode {
        const groupedByStartDate = _.groupBy(coverChanges, "startDate");

        let textLines: string[] = [];

        Object.entries(groupedByStartDate).forEach(([startDate, changes]) => {
            const textLinesCoverTier = this.transformCoverTierIntoTextLines(changes);
            const textLinesAddress = this.transformAddressChangeIntoTextLines(changes);
            const textLinesOptionalExtras = this.transformOptionalExtrasIntoTextLines(changes);
            const textLinesValuableItems =  this.transformValuableItemsIntoTextLines(changes);
            const textLinesMTA = this.transformMTAChangeIntoTextLines(changes);

            const coverChangesText = textLinesCoverTier
                .concat(textLinesAddress, textLinesOptionalExtras, textLinesValuableItems, textLinesMTA)
                .filter(Boolean)       // removes empty strings/null/undefined
                .join(", ");
            const line = this.convertDate(startDate, "DD/MM/YYYY") + ": " + _.upperFirst(coverChangesText);

            textLines.push(line);
        })

        const output = textLines.map((line: string, index) =>
            <li className="margin-bottom-8" key={index}>{line}</li>
        );
        return (<ul className="grid-col-2 m-0">{output}</ul>);
    }

    public transformCoverTierIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => coverChange.type === "COVER_TIER")
            .map(coverChange =>
                `${this.getChangedTierWord(coverChange)} cover tier from ${getCoverTierWording(coverChange.previousValue)} to ${getCoverTierWording(coverChange.currentValue)}`);
    }

    public transformAddressChangeIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => coverChange.type === "CHANGE_ADDRESS")
            .map(coverChange =>
                `Change of address from ${this.extractPostcode(coverChange.previousValue)} to ${this.extractPostcode(coverChange.currentValue)}`);
    }


    private extractPostcode(address: string): string {
        // UK postcode regex (covers standard formats)
        const postcodeRegex =
            /\b(([A-Z]{1,2}\d{1,2}[A-Z]?)\s?(\d[A-Z]{2}))\b/i;

        const match = postcodeRegex.exec(address);
        return match ? match[0].toUpperCase() : "";
    }


    private getChangedTierWord(coverChange: CoverChange) {
        const prevRank = InteractionHelper.getMotorTierRanking(coverChange.previousValue);
        const currRank = InteractionHelper.getMotorTierRanking(coverChange.currentValue);
        return currRank < prevRank ? "changed" : "upgraded";
    }

    private transformMTAChangeIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => this.isMTAChange(coverChange))
            .map(coverChange => this.getMtaChangeText(coverChange));
    }

    private getMtaChangeText(coverChange: CoverChange) {
        switch (coverChange.type) {
            case "REPLACE_CAR":
                return `replaced ${coverChange.previousValue}`;
            case "REPLACE_ELIGIBLE_CAR_FOR_ENHANCED_COURTESY_CAR_TO_INELIGIBLE":
                return `replaced ${coverChange.previousValue}`;
            case "REPLACE_INELIGIBLE_CAR_FOR_ENHANCED_COURTESY_CAR_TO_ELIGIBLE":
                return `replaced ${coverChange.previousValue}`;
            case "ADD_DRIVER":
                return `added ${coverChange.currentValue}`;
            case "EDIT_DRIVER":
                return `edited ${coverChange.currentValue}`;
            case "REMOVE_DRIVER":
                return `removed ${coverChange.currentValue}`;
            case "EDIT_CAR_DETAILS":
                return `edited car details`;
            case "PROPERTY_DETAILS_CHANGE":
                return `change of property details`;
            case "PROPERTY_USE_CHANGE":
                return `change of property use`;
            case "POLICYHOLDER_CHANGE":
                return `change of policyholder details`;
            case "FIXED_ADDON_CHANGE":
                return `fixed addon change`;
            default:
                return "";
        }
    }

    private transformOptionalExtrasIntoTextLines(coverChanges: CoverChange[]) {
        let optionalExtrasChanges = coverChanges.filter(coverChange => coverChange.type.startsWith("OPTIONAL_EXTRA"));

        if (!this.isBritannia) {
            optionalExtrasChanges = optionalExtrasChanges.filter(coverChange => coverChange.changedField !== "COURTESY_CAR_TOTAL_LOSS");
        }

        let lines: string[] = [];

        optionalExtrasChanges = this.handleModifiedPair(
            coverChanges,
            optionalExtrasChanges,
            lines,
            this.isBreakdownCover.bind(this),
            this.defaultModificationRules
        );

        optionalExtrasChanges = this.handleModifiedPair(
            coverChanges,
            optionalExtrasChanges,
            lines,
            this.isCourtesyCarWithLevel.bind(this),
            this.courtesyCarModificationRules
        );

        this.constructTextLines(optionalExtrasChanges, lines);

        return lines
    }

    private handleModifiedPair(
        coverChanges: CoverChange[],
        optionalExtrasChanges: CoverChange[],
        lines: string[],
        isTarget: (field: string) => boolean,
        modificationRules: Rule[]
    ): CoverChange[] {
        const added = optionalExtrasChanges.filter(
            (coverChange) => coverChange.action === "ADDED" && isTarget(coverChange.changedField)
        );
        const removed = optionalExtrasChanges.filter(
            (coverChange) => coverChange.action === "REMOVED" && isTarget(coverChange.changedField)
        );

        const context: ModificationContext = {
            added,
            removed,
            isIneligibleForEnhancedCourtesyCarReplacedByEligible: this.isIneligibleForEnhancedCourtesyCarReplacedByEligible(coverChanges),
            isEligibleForEnhancedCourtesyCarReplacedByIneligible: this.isEligibleForEnhancedCourtesyCarReplacedByIneligible(coverChanges)
        };

        const message = this.resolveModificationMessage(context, modificationRules);

        if (message) {
            lines.push(message);
            return optionalExtrasChanges.filter((c) => !isTarget(c.changedField));
        }

        return optionalExtrasChanges;
    }

    private resolveModificationMessage(context: ModificationContext, modificationRules: Rule[]): string | null {
        const rule = modificationRules.find(r => r.when(context));
        return rule ? rule.then(context) : null;
    }

    private readonly defaultModificationRules: Rule[] = [
        {
            when: (context) => this.isSinglePair(context),
            then: (context) =>
                `modified ${this.convertCoverType(context.removed[0])} to ${this.convertCoverType(context.added[0])}`
        }
    ];

    private readonly courtesyCarModificationRules: Rule[] = [
        {
            when: (context) =>
                context.isIneligibleForEnhancedCourtesyCarReplacedByEligible &&
                this.isSinglePair(context) &&
                context.removed[0].changedField === "COURTESY_CAR_TOTAL_LOSS" &&
                context.added[0].changedField === "ENHANCED_COURTESY_CAR",
            then: () => "updated guaranteed hire car from basic to enhanced (like-for-like), if your car is stolen or written off"
        },
        {
            when: (context) =>
                context.isEligibleForEnhancedCourtesyCarReplacedByIneligible &&
                this.isSinglePair(context) &&
                context.removed[0].changedField === "ENHANCED_COURTESY_CAR" &&
                context.added[0].changedField === "COURTESY_CAR_TOTAL_LOSS",
            then: () => "Enhanced (like-for-like) guaranteed hire car has been updated to Basic level due to size of vehicle"
        },
        ...this.defaultModificationRules,
        {
            when: (context) =>
                context.removed.length === 1 &&
                context.isIneligibleForEnhancedCourtesyCarReplacedByEligible &&
                this.isCourtesyCarWithLevel(context.removed[0].changedField),
            then: () => "removed basic guaranteed hire car if your car is stolen or written off"
        }
    ];

    private isEligibleForEnhancedCourtesyCarReplacedByIneligible(coverChanges: CoverChange[]) {
        return coverChanges.some(coverChange => coverChange.type === "REPLACE_ELIGIBLE_CAR_FOR_ENHANCED_COURTESY_CAR_TO_INELIGIBLE");
    }

    private isIneligibleForEnhancedCourtesyCarReplacedByEligible(coverChanges: CoverChange[]) {
        return coverChanges.some(coverChange => coverChange.type === "REPLACE_INELIGIBLE_CAR_FOR_ENHANCED_COURTESY_CAR_TO_ELIGIBLE");
    }

    private isSinglePair(context: ModificationContext) {
        return context.added.length === 1 && context.removed.length === 1;
    }

    private transformValuableItemsIntoTextLines(coverChanges: CoverChange[]) {
        let valuableItemsChanges = coverChanges.filter(coverChange => coverChange.type === "VALUABLE_ITEMS_CHANGE" || coverChange.type === "BICYCLES_CHANGE");

        let lines: string[] = [];

        this.constructTextLines(valuableItemsChanges, lines);

        return lines
    }

    private isBreakdownCover(coverType: string) {
        return coverType.startsWith("BREAKDOWN_COVER")
    }

    private isCourtesyCarWithLevel(coverType: string) {
        return this.isBritannia &&
            (coverType === "COURTESY_CAR_TOTAL_LOSS" || coverType === "ENHANCED_COURTESY_CAR");
    }

    private isMTAChange(coverChange: CoverChange) {
        const mtaChangeTypes = [
            "REPLACE_CAR",
            "REPLACE_ELIGIBLE_CAR_FOR_ENHANCED_COURTESY_CAR_TO_INELIGIBLE",
            "REPLACE_INELIGIBLE_CAR_FOR_ENHANCED_COURTESY_CAR_TO_ELIGIBLE",
            "ADD_DRIVER",
            "EDIT_DRIVER",
            "REMOVE_DRIVER",
            "CHANGE_ADDRESS",
            "EDIT_CAR_DETAILS",
            "PROPERTY_DETAILS_CHANGE",
            "PROPERTY_USE_CHANGE",
            "POLICYHOLDER_CHANGE",
            "FIXED_ADDON_CHANGE"
        ];
        return mtaChangeTypes.includes(coverChange.type);
    }

    private constructTextLines(optionalExtras: CoverChange[], lines: string[]) {
        optionalExtras.forEach(coverChange => {
            return lines.push(
                (this.convertOptionalExtraAction(coverChange) + " " + this.convertCoverType(coverChange)).toLowerCase()
            );
        });

        return lines;
    }

    private convertOptionalExtraAction(coverChange: CoverChange) {
        switch (coverChange.action) {
            case "ADDED":
                return "Added";
            case "REMOVED":
                return "Removed";
            default:
                return "";
        }
    }

    private readonly convertCoverType = (coverChange: CoverChange) => {
        const coverType = coverChange.changedField;

        switch (coverType) {
            case "BREAKDOWN_COVER_LOCAL":
                return "Breakdown cover (local)";
            case "BREAKDOWN_COVER_UK":
                return "Breakdown cover (national)";
            case "BREAKDOWN_COVER_EU":
                return "Breakdown cover (european)";
            case "FAMILY_PACKAGE":
                return "Family package";
            case "PROTECTED_NCD":
                return "Protected no claim discount";
            case "COURTESY_CAR":
                return "Guaranteed Hire Car";
            case "COURTESY_CAR_TOTAL_LOSS":
                return this.isBritannia && coverChange.type === "OPTIONAL_EXTRA_WITH_LEVEL" ?
                    "Guaranteed hire car if your car is stolen or written off (Basic)" :
                    "Guaranteed hire car if your car is stolen or written off";
            case "ENHANCED_COURTESY_CAR": {
                if (this.isBritannia) {
                    return "Guaranteed hire car if your car is stolen or written off (Enhanced)";
                }
                throw new Error(`Unexpected cover type: ` + coverType);
            }
            case "LEGAL_EXPENSES":
                return "Motor legal expenses";
            case "VALUABLE_ITEMS_CHANGE":
                return "Valuable item";
            case "BICYCLES_CHANGE":
                return "bicycle";
            default:
                throw new Error(`Unknown cover type: ` + coverType);
        }
    }

    private readonly convertDate = (inputDate: string, format: string) => {
        const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);
        if (valueAsMoment.isValid()) {
            return (valueAsMoment.format(format))
        }
        return null;
    };

    private readonly getNextPossibleMtaDate = (inputDate: string, format: string) => {
        const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);
        if (valueAsMoment.isValid()) {
            return (valueAsMoment.add(1, "days").format(format))
        }
        return null;
    };

    private hasChangesStartingToday(): boolean {
        const today = moment.utc();

        if (this.props.upcomingMtaDate) {
            return moment.utc(this.props.upcomingMtaDate).isSame(today, "day");
        }

        return this.state.coverChanges.some((change: CoverChange) =>
            moment.utc(change.startDate).isSame(today, "day")
        );
    }
}
